import { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react';
import { DropdownMenuComponent } from '../dropdown-menu/dropdown-menu.component';
import { IDropdownMenuItem, IFormField } from '@vivli/shared/infrastructure/interface';
import { Color, Size } from '@vivli/shared/theme';
import { BaseInputFieldComponent } from './base-input-field.component';
import { FormFieldTypeEnum } from '@vivli/shared/infrastructure/enum';
import mergeRefs from 'react-merge-refs';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import { Placement } from 'tippy.js';
import { EllipsisTextView } from '@vivli/shared/views';

const valueStyle = (readOnly?: boolean): CSSProperties => ({
    cursor: readOnly ? 'default' : 'pointer',
    display: 'flex',
    padding: '4px',
    paddingLeft: '15px',
    paddingRight: '60px',
    justifyContent: 'space-between',
    position: 'relative',
    fontSize: Size.FontSize.Large,
    color: Color.DARK_GRAY
});

interface DropdownFieldComponentProps extends IFormField {
    items: IDropdownMenuItem[];
    selectText?: string;
    onChange?: (value: any) => void;
    onReady?: () => void;
    placement?: Placement;
    sort?: 'asc' | 'desc';
    objectKey?: string;
    valueKey?: string;
    widthPerCharacter?: number;
    iconStyle?: CSSProperties;
}

export const DropdownFieldComponent = forwardRef((props: DropdownFieldComponentProps, ref) => {
    const dropdownRef = useRef<HTMLDivElement>();
    const [activeItems, setActiveItems] = useState<IDropdownMenuItem[]>([]);
    const [selectedItem, setSelectedItem] = useState<IDropdownMenuItem>(null);
    const {
        items,
        defaultValue,
        value,
        onChange,
        readonly,
        onBlur,
        placement,
        sort,
        selectText = '- Select an Option -',
        label,
        objectKey,
        dataId,
        name,
        valueKey,
        widthPerCharacter = 10,
        iconStyle = {}
    } = props;

    const downArrowStyle: CSSProperties = {
        position: 'absolute',
        right: '25px',
        bottom: '50%',
        ...iconStyle
    };

    const isObject = typeof value === 'object' || typeof defaultValue === 'object';
    const isControlled = value !== undefined;

    const setActiveItem = (dropdownMenuItem: IDropdownMenuItem) => {
        const resultItem = getItem(dropdownMenuItem.value);

        if (resultItem) {
            setSelectedItem(resultItem);
        }
    };

    const handleOnChange = (dropdownMenuItem: IDropdownMenuItem) => {
        let changedValue = dropdownMenuItem.value;

        // If need pass the only parameter instead of object
        if (valueKey) {
            changedValue = dropdownMenuItem.value[valueKey];
        }
        onChange && onChange(changedValue);
        setActiveItem(dropdownMenuItem);
    };

    const getItem = (comparisonValue) => {
        if ((valueKey || objectKey) && comparisonValue) {
            let key = valueKey || objectKey;
            if (typeof comparisonValue === 'object') {
                return activeItems?.find((i) => i.value[key] === comparisonValue[key]);
            }
            return activeItems?.find((i) => i.value[key] === comparisonValue);
        }
        return activeItems?.find((i) => i.value === comparisonValue);
    };

    useEffect(() => {
        if (!value && defaultValue) {
            const item = getItem(defaultValue);
            setSelectedItem(item);
        }
    }, []);

    /* ActiveItems can change at any time (before/after value/defaultValue has loaded)
     *  and will need to keep selected item.
     * defaultValue is in the dependency list because an uncontrolled caller whose value arrives or
     * changes after mount would otherwise never re-resolve, leaving the field showing a stale title
     * (or nothing at all) until it happened to remount. That is what made the uploader's File Type
     * cell render blank once its cell stopped being destroyed on every render. Calling
     * setSelectedItem with an equal value bails out, and this effect never writes activeItems, so it
     * cannot loop. (DEV-711) */
    useEffect(() => {
        const item = getItem(isControlled ? value : defaultValue);
        setSelectedItem(item);
    }, [value, defaultValue, activeItems]);

    /* Needs to check if the length of the two lists has changed to prevent
     *  redundant re-rendering when in use with areas like BulkUploadSelectors */
    useEffect(() => {
        if (!items || items.length === activeItems?.length) {
            return;
        }
        setActiveItems(items);
    }, [items]);

    /* A readonly dropdown renders as plain text rather than a disabled control. Two guards so it can
     * never collapse into an invisible element:
     *  - minHeight matches DropdownMenuComponent's clickAreaContainerStyle, so a readonly field takes
     *    the same space as an editable one and rows don't change height.
     *  - fall back to the raw value when it matches no item. Previously an unresolved selection
     *    rendered an empty ~8px div, which reads as "the field is missing". (DEV-711) */
    const getReadonlyText = () => {
        if (selectedItem?.title) {
            return selectedItem.title;
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof defaultValue === 'string') {
            return defaultValue;
        }
        return '';
    };

    const readonlyText = getReadonlyText();
    const readonlyDisplay = <div style={{ ...valueStyle(readonly), minHeight: '23px', alignItems: 'center' }}>{readonlyText}</div>;

    const buildInput = (_props) => (
        <DropdownMenuComponent
            placement={placement || 'bottom-end'}
            offset={[0, 0]}
            items={activeItems}
            onChange={handleOnChange}
            onBlur={onBlur}
            sort={sort}
            {..._props}
            dataId={dataId || name}
        >
            <div style={valueStyle(readonly)}>
                <EllipsisTextView text={selectedItem?.title || selectText} />
                <div style={downArrowStyle}>
                    <img src={AssetsConstant.DOWN_CHEVRON_BLACK} />
                </div>
            </div>
        </DropdownMenuComponent>
    );

    // prefer controlled value if it exists, then default
    const inputDisplay = value !== undefined ? buildInput({ value }) : buildInput({ defaultValue });

    return (
        <BaseInputFieldComponent {...props} type={FormFieldTypeEnum.DropDown} inputRef={dropdownRef} label={label || ''}>
            <div ref={mergeRefs([dropdownRef, ref])}>{!readonly ? inputDisplay : readonlyDisplay}</div>
        </BaseInputFieldComponent>
    );
});
